import { ENDCARD_FEED_MSG, ENDCARD_VIDEO_MSG } from "@config/quickNews";
import {
  loadLottieAnimation,
  LottieConfig,
  LottieInstance,
} from "@helper/lottieHelper";
import { FinishCardProps, QNCardType } from "@typings/QuickNews";
import { ReactElement, useEffect, useMemo, useRef } from "react";
import { twMerge } from "tailwind-merge";

type QNEndCardProps = {
  item: FinishCardProps;
  feedType: QNCardType;
  getOlderArticles?: () => void;
};

export function QnEndCard({
  item,
  feedType,
  getOlderArticles,
}: QNEndCardProps): ReactElement {
  const endCardInstanceRef = useRef<LottieInstance | null>(null);
  const readMoreInstanceRef = useRef<LottieInstance | null>(null);

  // Stable IDs, will not recreate on every render
  const { endCardCanvasId, readMoreCanvasId } = useMemo(
    () => ({
      endCardCanvasId: `end-card-canvas-${feedType}`,
      readMoreCanvasId: `qn-end-card-read-more-${item.id}`,
    }),
    [feedType, item.id],
  );

  useEffect(() => {
    let isMounted = true;

    const loadAnimations = () => {
      // End card animation
      const endCardConfig: LottieConfig = {
        animationPath: "quick-news/endCard.lottie",
        canvasId: endCardCanvasId,
        dimensions: { width: 300, height: 300, minHeight: 300 },
      };

      const endCardInstance = loadLottieAnimation(endCardConfig);
      if (isMounted) endCardInstanceRef.current = endCardInstance;

      // Read more animation (only for MyFeed)
      if (feedType === "MyFeed") {
        const readMoreConfig: LottieConfig = {
          animationPath: "quick-news/readMore.lottie",
          canvasId: readMoreCanvasId,
          dimensions: { height: 29, minHeight: 29 },
        };

        const readMoreInstance = loadLottieAnimation(readMoreConfig);
        if (isMounted) readMoreInstanceRef.current = readMoreInstance;
      }
    };

    loadAnimations();

    return () => {
      isMounted = false;

      endCardInstanceRef.current?.destroy();
      endCardInstanceRef.current = null;

      readMoreInstanceRef.current?.destroy();
      readMoreInstanceRef.current = null;
    };
  }, [endCardCanvasId, readMoreCanvasId, feedType]);

  const endCardMessage =
    feedType === "MyFeed" ? ENDCARD_FEED_MSG : ENDCARD_VIDEO_MSG;

  return (
    <div className="w-full h-full bg-transparent" key={item.id}>
      <div className="grid grid-rows-5 h-full">
        {/* Animation Canvas */}
        <div className="row-span-3 flex items-center justify-center">
          <canvas id={endCardCanvasId} className="m-auto" />
        </div>

        {/* Text Message */}
        <div className="row-span-2 text-center mx-auto w-[60%] text-white-100 leading-tight">
          <span className="text-md lg:text-lg font-semibold">
            Syabas! {endCardMessage}
          </span>
        </div>
      </div>

      {/* Read More Button for MyFeed */}
      {feedType === "MyFeed" && (
        <button
          aria-label="Next card"
          className="absolute bottom-4 w-full text-white-100 flex flex-col items-center justify-center"
          onClick={getOlderArticles}
        >
          Swipe untuk baca lebih banyak berita
          <canvas
            id={readMoreCanvasId}
            className={twMerge("mx-auto w-[11px]")}
          />
        </button>
      )}
    </div>
  );
}
